/**
 * Кэш тяжёлых шагов отчёта по mediaId.
 *
 * Оба шага считаются на каждую публикацию и оба неизменны: перезалитая в
 * Storage превьюшка и тема, распознанная с кадра. Публикация, которая один раз
 * попала в отчёт, второй раз считаться не должна.
 *
 * Экономия видна сразу в двух местах:
 *  - «Сформировать отчёт» и «Отчёт министру» — две кнопки по одним и тем же
 *    данным, и вторая гоняла весь проход распознавания заново;
 *  - отчёты за пересекающиеся периоды (вчера-сегодня, неделя-месяц) заново
 *    распознавали те же публикации.
 *
 * Живёт в localStorage: он переживает перезагрузку страницы, а больше от него
 * ничего и не нужно. Общего кэша на команду тут нет — это следующий шаг,
 * когда сбор переедет на бэкенд.
 */

const KEY = 'instafarm.report-cache.v1';
/** Больше не храним: примерно год работы одного аккаунта. */
const MAX_ENTRIES = 5000;

export interface ReportCacheEntry {
  /** Перезалитый в Storage адрес превью. */
  imageUrl?: string;
  /** Тема публикации и её язык. */
  theme?: string;
  language?: string;
}

export type ReportCache = Map<string, ReportCacheEntry>;

/**
 * Оставляет последние `limit` записей. Порядок в Map — порядок вставки,
 * поэтому свежие записи всегда в хвосте, а вытесняются самые давние.
 */
export function pruneCache(cache: ReportCache, limit = MAX_ENTRIES): ReportCache {
  if (cache.size <= limit) return cache;
  const keep = Array.from(cache.entries()).slice(cache.size - limit);
  return new Map(keep);
}

export function loadReportCache(): ReportCache {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return new Map();
    const obj = JSON.parse(raw) as Record<string, ReportCacheEntry>;
    return new Map(Object.entries(obj));
  } catch {
    // Приватное окно, запрет на хранилище, битый JSON — кэш просто не работает.
    return new Map();
  }
}

export function saveReportCache(cache: ReportCache): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(pruneCache(cache))));
  } catch {
    // Переполнение квоты или запрет на запись — отчёт всё равно построится.
  }
}

export interface ReportWorkItem {
  mediaId: string;
  thumbnailUrl?: string;
}

export interface ReportWorkPlan<T extends ReportWorkItem> {
  /** Превью, которые ещё не перезаливали. */
  imagesToFetch: { id: string; thumbnailUrl: string }[];
  /** Готовые адреса превью из кэша. */
  imagesFromCache: Map<string, string>;
  /** Публикации, тему которых ещё предстоит распознать. */
  themesToCompute: T[];
  /** Готовые темы из кэша. */
  themesFromCache: Map<string, { theme: string; language: string }>;
  /** Сколько шагов реально предстоит выполнить — для прогресса. */
  totalWork: number;
}

/**
 * Делит работу отчёта на «уже посчитано» и «ещё считать».
 *
 * Вынесено из компонента, потому что здесь легко ошибиться на пустых значениях:
 * тема бывает пустой строкой, и проверка на истинность вместо проверки на
 * наличие заставляла бы распознавать такую публикацию заново каждый раз.
 */
export function planReportWork<T extends ReportWorkItem>(
  items: readonly T[],
  cache: ReportCache,
): ReportWorkPlan<T> {
  const imagesToFetch: { id: string; thumbnailUrl: string }[] = [];
  const imagesFromCache = new Map<string, string>();
  const themesToCompute: T[] = [];
  const themesFromCache = new Map<string, { theme: string; language: string }>();

  for (const item of items) {
    const hit = cache.get(item.mediaId);

    if (hit?.imageUrl) imagesFromCache.set(item.mediaId, hit.imageUrl);
    else if (item.thumbnailUrl) imagesToFetch.push({ id: item.mediaId, thumbnailUrl: item.thumbnailUrl });

    // Именно !== undefined: пустая тема — это тоже посчитанный результат.
    if (hit?.theme !== undefined) themesFromCache.set(item.mediaId, { theme: hit.theme, language: hit.language ?? '' });
    else themesToCompute.push(item);
  }

  return {
    imagesToFetch,
    imagesFromCache,
    themesToCompute,
    themesFromCache,
    totalWork: imagesToFetch.length + themesToCompute.length,
  };
}

/**
 * Можно ли запомнить адрес превью навсегда.
 *
 * Перезалитое в Storage превью лежит по постоянному публичному адресу — его
 * кэшируем. Но когда перезалить не удалось, бэкенд отдаёт исходную ссылку
 * Instagram, а она подписана и протухает примерно за сутки. Запомнив такую,
 * мы бы навсегда оставили в отчёте картинку, которая завтра не откроется:
 * раньше следующий отчёт перезаливал превью заново и разовый сбой лечился сам.
 */
export function isCacheableImageUrl(imageUrl: string, sourceThumbnailUrl?: string): boolean {
  if (!imageUrl) return false;
  return imageUrl !== sourceThumbnailUrl;
}
